import { ICustomFieldResSchema } from '@monetize/types/app';
import { FilterType } from '@monetize/types/common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Link as ReactDomLink } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useGetListData } from '../../../../api/queryUtils';
import {
  MBox,
  MButton,
  MFlex,
  MPageContainer,
  MSettingsPageHeader,
} from '../../../../components/Monetize';
import { useTableColumns } from '../../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../../../components/Monetize/DataTableNew/DataTableTypes';
import { ROUTES } from '../../../../constants';
import { useACL } from '../../../../services/acl/acl';
import { useDocumentHead } from '../../../../services/documentHead';
import {
  tableFilterSelector,
  tablePagerSelector,
} from '../../../../store/global.store';
import { FilterStateKeys } from '../../../../store/store.types';
import {
  buildFilterParamsRequestObject,
  getFiltersApplied,
} from '../../../../utils';
import { navigateOrOpenNewWindow } from '../../../../utils/misc';
import {
  CUSTOM_FIELDS_LIST_CATALOG,
  CustomFieldsListColumnContext,
} from './customFieldsList.columns';

export const CustomFieldsPage = () => {
  const navigate = useNavigate();
  const { setDocTitle } = useDocumentHead();
  const { canDo } = useACL();

  const [initialFilters, persistTableFilter] = useAtom(
    tableFilterSelector(FilterStateKeys.CUSTOM_FIELDS_LIST),
  );

  const [pager, setPager] = useAtom(
    tablePagerSelector(FilterStateKeys.CUSTOM_FIELDS_LIST),
  );

  const [filters, setFilters] = useState<FilterType[]>(
    initialFilters as FilterType[],
  );
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [searchKey] = useState<string>('displayLabel');
  const filterParams = useMemo(
    () => buildFilterParamsRequestObject(filters, globalFilter, searchKey),
    [filters, globalFilter, searchKey],
  );

  const {
    data: customFieldList,
    isLoading,
    isFetched,
    isRefetching,
    refetch,
    isError,
    error,
  } = useGetListData<ICustomFieldResSchema>('customFields', {
    config: pager,
    filters: filterParams,
  });

  const loading = isLoading || (isRefetching && !isFetched);

  const tableRef = useRef<DataTableRef>(null);

  const onResetFilter = useCallback(() => {
    setFilters([]);
    tableRef.current?.resetFilters();
  }, [setFilters]);

  useEffect(() => {
    persistTableFilter(filters);
  }, [filters, persistTableFilter]);

  useEffect(() => {
    setDocTitle('Settings', 'Custom Fields');
  }, []);

  const hasFiltersOrSearchApplied =
    getFiltersApplied(filters) > 0 || !!globalFilter;

  const columnContext = useMemo<CustomFieldsListColumnContext>(() => ({}), []);
  const { columns, state: columnState } = useTableColumns(
    CUSTOM_FIELDS_LIST_CATALOG,
    columnContext,
  );

  return (
    <MPageContainer data-testid="customfields" alignItems="stretch">
      <MSettingsPageHeader title="Custom Fields">
        <MFlex>
          <MButton variant="primary" ml="2" asChild>
            <ReactDomLink to={ROUTES.CUSTOM_FIELDS_NEW}>New</ReactDomLink>
          </MButton>
        </MFlex>
      </MSettingsPageHeader>
      <MBox width="100%">
        <DataTable
          ref={tableRef}
          columns={columns}
          state={columnState}
          data={customFieldList?.content ?? []}
          isLoading={loading}
          enablePagination
          pinnedRightColumns={[ACTION_COLUMN_KEY]}
          totalElements={customFieldList?.totalElements}
          totalPages={customFieldList?.totalPages}
          pager={pager}
          setPager={setPager}
          initialFilters={filters}
          onFiltersChange={(newFilters) => {
            setFilters(newFilters);
          }}
          onRowClick={(row, _tableRow, openInNewWindow) =>
            navigateOrOpenNewWindow(
              navigate,
              ROUTES.getCustomFieldEditRoute(row.entity, row.key),
              openInNewWindow,
            )
          }
          emptyProps={{
            mainMessage: 'Looks like there are no Custom Fields here.',
            smallMessage: '',
            btnLabel:
              !hasFiltersOrSearchApplied && canDo([['settings', 'create']])
                ? 'New Custom Field'
                : null,
            to: ROUTES.CUSTOM_FIELDS_NEW,
          }}
          emptyFilterResultsProps={{
            mainMessage: 'Looks like there are no Custom Fields here.',
            smallMessage: null,
            btnLabel: null,
            secondaryBtnLabel: 'Clear Search and Filters',
            onReload: onResetFilter,
          }}
          resetFilter={onResetFilter}
        />
      </MBox>
    </MPageContainer>
  );
};
